<template>
  <div :class="['sp_v2_playlist_drawer', { open: isOpen }]" v-if="isOpen">
    <div class="drawer-content">
      <div class="drawer-header">
        <div class="header-info">
          <h6>My Playlist</h6>
          <div class="info-holder">
            <span>{{ playlist.length }} {{ playlist.length === 1 ? "topic" : "topics" }}</span> · <span>{{ formattedDuration }}</span>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="drawer-loading">
        <div class="skeleton-item" v-for="i in 3" :key="i"></div>
      </div>

      <div v-else-if="playlist.length === 0" class="drawer-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 2H15M15 2V6M15 2L19 6M9 2V6M9 2L5 6M5 6V20C5 21.1046 5.89543 22 7 22H17C18.1046 22 19 21.1046 19 20V6M5 6H19M9 11H15M9 15H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <p>No content available</p>
        <span class="empty-hint">Content will appear here when available</span>
      </div>

      <ul v-else class="playlist-items">
        <li v-for="content in playlist" :key="`${content.content_id}-${content.content_type}`" :class="['playlist-item animated fadeIn', { active: isActiveContent(content) }]" @click="selectContent(content)">
          <div class="item-thumbnail">
            <img :src="content.image" :alt="content.name" />
          </div>
          <div class="item-info">
            <h6 class="item-name" :title="content.name">{{ content.name }}</h6>
            <span class="item-type">
              <span>{{ getContentTypeLabel(content.content_extension) }}</span>
              <span v-if="getContentDuration(content)"> · {{ getContentDuration(content) }}</span>
            </span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import MediaQueryMixin from "../mixins/mediaQuery.js";

export default {
  name: "PlaylistDrawerV2",
  mixins: [MediaQueryMixin],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    playlist: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedContent: {
      type: Object,
      default: null,
    },
  },
  methods: {
    selectContent(content) {
      this.$emit("select", content);
    },
    isActiveContent(content) {
      if (!this.selectedContent) return false;
      return this.selectedContent.content_id === content.content_id && this.selectedContent.content_type === content.content_type;
    },
    getContentTypeLabel(extension) {
      const labels = {
        pdf: "PDF",
        video: "Video",
        youtube: "YouTube",
        image: "Image",
        "web page": "Web page",
        html: "HTML",
        article: "Article",
        tour: "Interactive",
      };
      return labels[extension] || extension.charAt(0).toUpperCase() + extension.slice(1).toLowerCase();
    },
    getContentDuration(content) {
      const seconds = content.duration || content.duration_seconds || 0;
      if (!seconds) return null;
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      if (minutes > 0 && secs > 0) return `${minutes}min ${secs}sec`;
      if (minutes > 0) return `${minutes}min`;
      return `${secs}sec`;
    },
    formatDuration(seconds) {
      if (!seconds) return "0min 0sec";
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes}min ${secs}sec`;
    },
  },
  computed: {
    formattedDuration() {
      if (!this.playlist || this.playlist.length === 0) return "0min 0sec";

      const totalSeconds = this.playlist.reduce((total, item) => {
        const duration = item.duration || item.duration_seconds || 0;
        return total + (typeof duration === "number" ? duration : 0);
      }, 0);

      return this.formatDuration(totalSeconds);
    },
  },
};
</script>

<style lang="scss" scoped>
.sp_v2_playlist_drawer {
  width: 280px;
  min-width: 280px;
  height: 100%;
  border-right: 1px solid #e5e7ea;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.drawer-header {
  padding: 20px 16px 12px;
  flex-shrink: 0;

  .header-info {
    h6 {
      font-size: 14px;
      font-weight: 700;
      color: #060e2b;
      margin: 0 0 4px 0;
      line-height: 1.2;
    }

    .info-holder {
      font-size: 12px;
      color: #6b7280;
      display: block;
    }
  }
}

.playlist-items {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 12px 12px;
  list-style: none;
  margin: 0;
  width: 100%;
  box-sizing: border-box;

  scrollbar-width: thin;
  scrollbar-color: #e5e7ea transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e5e7ea;
    border-radius: 2px;
  }
}

.playlist-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  margin-bottom: 4px;
  border: 2px solid transparent;
  border-left: 3px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-sizing: border-box;

  &:hover {
    background: #f9fafb;
    border-left-color: var(--sp-chat-primary);
  }

  &.active {
    background: #f0f3ff;
    border-left-color: var(--sp-chat-primary);
  }

  .item-thumbnail {
    position: relative;
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 8px;
    overflow: hidden;

    img {
      width: 56px;
      height: 56px;
      object-fit: cover;
      border-radius: 8px;
    }
  }

  .item-info {
    flex: 1;
    min-width: 0;

    .item-name {
      font-size: 13px;
      font-weight: 600;
      color: #060e2b;
      padding: 0;
      margin: 0 0 4px 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.3;
    }

    .item-type {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      color: #6b7280;
      font-size: 12px;
      font-weight: 400;
    }
  }
}

.drawer-loading {
  padding: 12px;

  .skeleton-item {
    height: 50px;
    background: linear-gradient(90deg, #f2f3f5 25%, #e5e7ea 50%, #f2f3f5 75%);
    background-size: 200% 100%;
    animation: skeletonPulse 1.5s infinite;
    border-radius: 6px;
    margin-bottom: 8px;
  }
}

.drawer-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .empty-hint {
    font-size: 12px;
    color: #9ca3af;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    background-position: 200% 0;
  }
  50% {
    background-position: -200% 0;
  }
}

.animated {
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

.fadeIn {
  animation-name: fadeIn;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 1023px) {
  .sp_v2_playlist_drawer {
    width: 100%;
    min-width: 100%;
    height: max-content;
    border-right: none;
    border-bottom: 1px solid #e5e7ea;
  }

  .drawer-header {
    display: none;
  }

  .playlist-items {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding: clamp(6px, 2vw, 12px);
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .playlist-item {
    flex-shrink: 0;
    flex-direction: row;
    align-items: center;
    min-width: clamp(140px, 45vw, 220px);
    width: clamp(140px, 45vw, 220px);
    padding: clamp(8px, 2vw, 12px);
    margin-bottom: 0;
    margin-right: clamp(6px, 2vw, 12px);
    border-left: 3px solid transparent;

    &:last-child {
      margin-right: 12px;
    }

    &:hover,
    &.active {
      border-left-color: var(--sp-chat-primary);
    }

    .item-thumbnail {
      width: clamp(40px, 12vw, 56px);
      height: clamp(40px, 12vw, 56px);
      flex-shrink: 0;

      img {
        width: clamp(40px, 12vw, 56px);
        height: clamp(40px, 12vw, 56px);
      }
    }

    .item-info {
      .item-name {
        font-size: clamp(11px, 3vw, 13px);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item-type {
        font-size: clamp(10px, 2.5vw, 12px);
      }
    }
  }
}
</style>
